import { z } from 'zod';

// Parking tab — GET /dashboard/parking/metrics.

export const ParkingSummarySchema = z.object({
  transaction_count: z.number().int().nonnegative(),
  gross_parking_revenue: z.number().int().nonnegative(),
  net_parking_revenue: z.number().int().nonnegative(),
  refunded_transaction_count: z.number().int().nonnegative(),
  refunded_transaction_amount: z.number().int().nonnegative(),
});

export const ParkingTrendsSchema = z.object({
  transactions_per_hour: z.array(z.number().int().nonnegative()),
});

export const ParkingOccupancySchema = z.object({
  active_reservation_count: z.number().int().nonnegative(),
  total_capacity: z.number().int().nonnegative(),
  occupancy_percent: z.number().nonnegative(),
});

export const ParkingMetricsSchema = z.object({
  summary: ParkingSummarySchema,
  trends: ParkingTrendsSchema,
  occupancy: ParkingOccupancySchema,
});

export type ParkingMetricsType = z.infer<typeof ParkingMetricsSchema>;

// Enforcement tab — GET /dashboard/enforcement/metrics.

export const EnforcementSummarySchema = z.object({
  issued_violation_count: z.number().int().nonnegative(),
  issued_warning_count: z.number().int().nonnegative(),
  voided_violation_count: z.number().int().nonnegative().optional().default(0),
  paid_violation_count: z.number().int().nonnegative(),
  violation_amount_collected: z.number().int().nonnegative(),
  // collected ÷ issued across the resolved locations for the period.
  // 0 when no notices were issued. Drives the Collection Rate KPI tile.
  collection_rate: z.number().min(0).max(1),
});

// Bucket granularity is picked server-side from the requested period
// length (hour for ≤1 day, day for ≤31 days, month otherwise). Server
// backfills empty buckets with zero so every array is the same length
// as buckets[].
// .catch('day') hardens against the server adding a new bucket value
// (e.g. 'week', 'quarter') — the whole tab payload would otherwise fail
// Zod validation. Day is the safest fallback because the daily axis
// formatter ('LLL d') is the most generic. Matches the resilient-enum
// pattern used by AccessDecisionSourceEnum elsewhere in this file.
export const TrendBucketSchema = z.enum(['hour', 'day', 'month']).catch('day');
export type TrendBucketType = z.infer<typeof TrendBucketSchema>;

// Status trends powers the stacked-area chart of violation status counts.
// Renamed from EnforcementTrendsSchema once collection_trends joined the
// payload — the generic "trends" name became ambiguous.
export const EnforcementStatusTrendsSchema = z.object({
  bucket: TrendBucketSchema,
  buckets: z.array(z.string()),
  unapproved: z.array(z.number().int().nonnegative()),
  confirmed: z.array(z.number().int().nonnegative()),
  paid: z.array(z.number().int().nonnegative()),
  voided: z.array(z.number().int().nonnegative()),
  waived: z.array(z.number().int().nonnegative()),
});

// Collection trends powers the collection-rate line chart. Notice-date
// cohort: a violation is bucketed by its first parkpliant 'notice' event
// falling in the bucket. Collected counts that cohort regardless of when
// the invoice was paid; the rate per bucket is derived client-side as
// collected[i] / issued[i] (null when issued[i] is 0, rendered as a gap).
//
// cohort_maturity_days is the trailing window during which the rate is
// provisional — notices sent within today − cohort_maturity_days haven't
// had time to convert. The chart dims those trailing buckets.
export const EnforcementCollectionTrendsSchema = z.object({
  bucket: TrendBucketSchema,
  buckets: z.array(z.string()),
  issued: z.array(z.number().int().nonnegative()),
  collected: z.array(z.number().int().nonnegative()),
  cohort_maturity_days: z.number().int().nonnegative(),
});

// One row per location for the Collection Breakdown table. Empty `[]`
// when the resolved location filter contains a single location — the
// portal omits the table in that case (mirrors MonthlyParkerBreakdown).
//
// Rates are 0..1 floats. cadence_avg_days is the avg gap between
// consecutive notices on cohort violations; 0 when no multi-notice
// violation exists at the location (rendered as "—", not "0 days").
export const EnforcementCollectionBreakdownSchema = z.object({
  location_id: z.string(),
  location_nickname: z.string(),
  issued: z.number().int().nonnegative(),
  collected: z.number().int().nonnegative(),
  collection_rate: z.number().min(0).max(1),
  dispute_void_rate: z.number().min(0).max(1),
  cadence_avg_days: z.number().nonnegative(),
});

export const EnforcementMetricsSchema = z.object({
  summary: EnforcementSummarySchema,
  status_trends: EnforcementStatusTrendsSchema,
  collection_trends: EnforcementCollectionTrendsSchema,
  collection_breakdown: z.array(EnforcementCollectionBreakdownSchema),
});

export type EnforcementMetricsType = z.infer<typeof EnforcementMetricsSchema>;
export type EnforcementStatusTrendsType = z.infer<
  typeof EnforcementStatusTrendsSchema
>;
export type EnforcementCollectionTrendsType = z.infer<
  typeof EnforcementCollectionTrendsSchema
>;
export type EnforcementCollectionBreakdownType = z.infer<
  typeof EnforcementCollectionBreakdownSchema
>;

// Monthly Parkers tab — GET /dashboard/monthly/metrics.

export const MonthlyParkerSummarySchema = z.object({
  total_active_subscribers: z.number().int().nonnegative(),
  total_visits: z.number().int().nonnegative().optional().default(0),
  avg_visits_per_subscriber: z.number().nonnegative(),
  zero_visit_subscribers: z.number().int().nonnegative(),
});

// Server emits one row per location when the resolved location filter
// contains >1 location; the slice is empty (`[]`) when 1 location is
// selected so the portal can omit the breakdown table without client-side
// "is there enough" branching.
export const MonthlyParkerBreakdownSchema = z.object({
  location_id: z.string(),
  location_nickname: z.string(),
  active_subscribers: z.number().int().nonnegative(),
  total_visits: z.number().int().nonnegative(),
  avg_visits_per_subscriber: z.number().nonnegative(),
  zero_visit_subscribers: z.number().int().nonnegative(),
});

export const MonthlyParkerTrendsSchema = z.object({
  // Aligned bucket / visits arrays — server picks granularity from the
  // requested period length (hour / day / month).
  bucket: TrendBucketSchema,
  buckets: z.array(z.string()),
  visits: z.array(z.number().int().nonnegative()),
});

export const MonthlyParkerMetricsSchema = z.object({
  summary: MonthlyParkerSummarySchema,
  breakdown: z.array(MonthlyParkerBreakdownSchema),
  trends: MonthlyParkerTrendsSchema,
});

export type MonthlyParkerMetricsType = z.infer<
  typeof MonthlyParkerMetricsSchema
>;
export type MonthlyParkerBreakdownType = z.infer<
  typeof MonthlyParkerBreakdownSchema
>;
export type MonthlyParkerSummaryType = z.infer<
  typeof MonthlyParkerSummarySchema
>;

// PPI abandonment-drip funnel — GET /dashboard/enforcement/drip-funnel (ENG-1955).

// Counts and rates share this shape between the per-row breakdown and the
// rolled-up totals, so both render through the same formatters.
//
// sent / delivered / opened / clicked count drip touches — one per violation per
// touch index — so a touch that had to be re-sent is not counted twice.
// pay_link_clicks / evidence_link_clicks count individual click events, since
// opening the evidence package twice is real engagement.
//
// paid_attributed counts violations paid within the attribution window after a
// click on that touch; paid_any counts every paid violation in the cohort. The
// pair is what separates drip-driven payment from background payment, so the UI
// must never show one without the other.
const DripFunnelCountsSchema = z.object({
  sent: z.number().int().nonnegative(),
  delivered: z.number().int().nonnegative(),
  opened: z.number().int().nonnegative(),
  clicked: z.number().int().nonnegative(),
  pay_link_clicks: z.number().int().nonnegative(),
  evidence_link_clicks: z.number().int().nonnegative(),
  paid_attributed: z.number().int().nonnegative(),
  paid_any: z.number().int().nonnegative(),
});

// Rates are 0..1 floats derived server-side from the counts; an empty cohort
// yields 0 rather than a division by zero. avg_hours_to_pay is the mean
// click-to-payment gap over the attributed violations, 0 when none.
const DripFunnelRatesSchema = z.object({
  delivery_rate: z.number().min(0).max(1),
  open_rate: z.number().min(0).max(1),
  click_rate: z.number().min(0).max(1),
  paid_conversion: z.number().min(0).max(1),
  avg_hours_to_pay: z.number().nonnegative(),
});

// One row per location and drip touch. touch_index is the touch's position in
// the cadence, 0-based.
export const DripFunnelBreakdownSchema = z
  .object({
    location_id: z.string(),
    location_nickname: z.string(),
    touch_index: z.number().int().nonnegative(),
  })
  .merge(DripFunnelCountsSchema)
  .merge(DripFunnelRatesSchema);

export const DripFunnelMetricsSchema = z.object({
  // The window the paid-conversion numbers were computed with, so an exported
  // table is self-describing rather than silently assuming the default.
  attribution_window_hours: z.number().int().positive(),
  totals: DripFunnelCountsSchema.merge(DripFunnelRatesSchema),
  breakdown: z.array(DripFunnelBreakdownSchema),
});

export type DripFunnelMetricsType = z.infer<typeof DripFunnelMetricsSchema>;
export type DripFunnelBreakdownType = z.infer<typeof DripFunnelBreakdownSchema>;
